import { type InfiniteData } from '@tanstack/react-query';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import ms from 'ms';
import { useEffect, useState } from 'react';

import { toast } from '@hivebrite/design-system/Toast';

import {
  type FeedItem as FeedItemType,
  type FetchItemsResponse,
} from 'hb-react/frontoffice/apps/LiveFeed/components/Feed/types';
import deletePost from 'hb-react/frontoffice/apps/LiveFeed/utils/deletePost';

interface UseInfiniteFeedItemsProps {
  currentFilter: any;
  fetchItems: (any) => Promise<FetchItemsResponse>;
  initialPageParam?: number;
  maxPages?: number;
  onUserCanNotAccessLiveFeed?: () => void;
  perPage?: number;
  routes: { feed_items_path: (params: unknown) => string };
  topicId?: number;
}

const getQueryKey = (
  routes: { feed_items_path: (params: unknown) => string },
  currentFilter: any,
  initialPageParam: number,
  topicId?: number,
) => [
  'feed',
  routes?.feed_items_path?.('*') ?? 'feed_items_path',
  JSON.stringify(currentFilter),
  initialPageParam,
  topicId,
];

export const useInfiniteFeedItems = ({
  currentFilter,
  fetchItems,
  initialPageParam = 1,
  maxPages = 10,
  perPage = 10,
  routes,
  topicId,
  onUserCanNotAccessLiveFeed,
}: UseInfiniteFeedItemsProps) => {
  const [total, setTotal] = useState<number>();

  const queryClient = useQueryClient();
  const queryKey = getQueryKey(
    routes,
    currentFilter,
    initialPageParam,
    topicId,
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isLoading,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      try {
        return await fetchItems({
          routes,
          page: pageParam,
          perPage,
          filters: currentFilter?.value,
          onUserCanNotAccessLiveFeed,
        });
      } catch (error) {
        toast.error(__('Sorry, an error occurred'));
        throw error;
      }
    },
    getNextPageParam: (lastPage, allPages) => {
      const noItemsInTheLastPage = lastPage && lastPage.items?.length === 0;
      const hasNoMoreItems =
        lastPage && lastPage.total <= allPages.length * perPage;
      const hasReachedMaxPages = maxPages && allPages.length >= maxPages;

      if (noItemsInTheLastPage || hasNoMoreItems || hasReachedMaxPages) {
        return undefined;
      }

      return initialPageParam + allPages.length;
    },
    initialPageParam,
    staleTime: ms('5m'),
    refetchOnMount: true,
  });

  useEffect(() => {
    if (data?.pages?.length) {
      setTotal(data.pages[data.pages.length - 1].total);
    } else {
      setTotal(undefined);
    }
  }, [data]);

  const filterItems = (filterFn) => {
    queryClient.setQueryData<InfiniteData<FetchItemsResponse> | undefined>(
      queryKey,
      (oldData) => {
        if (oldData) {
          const pages = oldData.pages.map((page) => {
            const items = page.items.filter(filterFn);

            return {
              ...page,
              items,
            };
          });

          return {
            ...oldData,
            pages,
          };
        }
      },
    );
  };

  const updateItem = (newItem: Partial<FeedItemType>) => {
    if (!newItem?.id || !newItem?.type) {
      return;
    }

    queryClient.setQueryData<InfiniteData<FetchItemsResponse> | undefined>(
      queryKey,
      (oldData) => {
        if (oldData) {
          const pages = oldData.pages.map((page) => {
            const items = page.items.map((item) => {
              const isUpdatedItem =
                item.id === newItem.id && item.type === newItem.type;

              if (isUpdatedItem) {
                return {
                  ...item,
                  ...newItem,
                };
              }

              return item;
            });

            return {
              ...page,
              items,
            };
          });

          return {
            ...oldData,
            pages,
          };
        }
      },
    );
  };

  const { mutateAsync: deleteItemMutation } = useMutation({
    mutationFn: (item: FeedItemType) =>
      deletePost({
        postId: item.postId,
        routes,
        topicId: item.topicId,
        type: item.type,
      })
        // Pass to onSuccess the item as parameter
        .then(() => item),
    onSuccess: (item) => {
      filterItems((i) => i.id !== item.postId || i.type !== item.type);
    },
  });

  return {
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isError,
    data,
    total,
    deleteItemMutation,
    filterItems,
    updateItem,
  };
};
